import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { formatPrice, formatNumber } from '@/lib/formatters';
import { Bed, Bath, Square, ChevronDown, ChevronUp, Trophy, Sparkles, Clock, Camera } from 'lucide-react';
import { useState } from 'react';
import { RankedListing } from '@/hooks/useRankedListings';
import { cn } from '@/lib/utils';
import { PropertyImageCarousel } from '@/components/property/PropertyImageCarousel';
import { getPropertyUrl } from '@/types/property';

interface RankedPropertyCardProps {
  rankedListing: RankedListing;
  showBreakdown?: boolean;
  communityName?: string;
}

export function RankedPropertyCard({ rankedListing, showBreakdown = true, communityName }: RankedPropertyCardProps) {
  const [expanded, setExpanded] = useState(false);
  const { property, rank, score_total, score_breakdown } = rankedListing;

  if (!property) return null;

  const primaryImage = property.images?.[0] || '/placeholder.svg';
  const photoCount = property.images?.length || 0;

  const getRankBadgeStyle = (rank: number) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-500 to-amber-500 text-white';
    if (rank === 2) return 'bg-gradient-to-r from-gray-400 to-gray-500 text-white';
    if (rank === 3) return 'bg-gradient-to-r from-amber-600 to-amber-700 text-white';
    return 'bg-primary/10 text-primary';
  };

  const getTopReasons = () => {
    if (!score_breakdown) return [];
    
    const reasons: { icon: typeof Trophy; text: string; score: number }[] = [];
    
    if (score_breakdown.price?.score >= 80) {
      reasons.push({ icon: Sparkles, text: score_breakdown.price.reason, score: score_breakdown.price.score });
    }
    if (score_breakdown.photos?.score >= 80) {
      reasons.push({ icon: Camera, text: score_breakdown.photos.reason, score: score_breakdown.photos.score });
    }
    if (score_breakdown.recency?.score >= 80) {
      reasons.push({ icon: Clock, text: score_breakdown.recency.reason, score: score_breakdown.recency.score });
    }
    
    return reasons.slice(0, 3);
  };

  const topReasons = getTopReasons();

  return (
    <Card className="overflow-hidden group hover:shadow-lg transition-shadow duration-300">
      <Link to={getPropertyUrl(property)} className="block">
        <div className="relative aspect-[4/3]">
          <PropertyImageCarousel
            images={property.images}
            alt={property.title || property.address}
            showDots={false}
          />
          
          {/* Rank Badge */}
          <Badge 
            className={cn(
              'absolute top-3 left-3 text-sm font-bold px-3 py-1 shadow-lg',
              getRankBadgeStyle(rank)
            )}
          >
            <Trophy className="w-4 h-4 mr-1" />
            #{rank}
          </Badge>

          {/* Photo Count */}
          {photoCount > 0 && (
            <Badge className="absolute top-3 right-3 bg-black/60 text-white">
              <Camera className="w-3 h-3 mr-1" />
              {photoCount}
            </Badge>
          )}

          {/* Score */}
          <div className="absolute bottom-3 right-3 bg-black/70 text-white px-2 py-1 rounded text-sm font-medium">
            Score: {Math.round(score_total)}
          </div>
        </div>
      </Link>

      <CardContent className="p-4">
        <Link to={getPropertyUrl(property)}>
          <div className="text-xl font-bold text-primary mb-1">
            {formatPrice(property.price)}
          </div>
          
          <h3 className="font-medium text-foreground line-clamp-1 group-hover:text-primary transition-colors">
            {property.address}
          </h3>
          
          {(communityName || property.neighborhood) && (
            <p className="text-sm text-muted-foreground mb-2">
              {communityName || property.neighborhood}{property.city ? `, ${property.city}` : ''}
            </p>
          )}

          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
            <span className="flex items-center gap-1">
              <Bed className="w-4 h-4" />
              {property.bedrooms} beds
            </span>
            <span className="flex items-center gap-1">
              <Bath className="w-4 h-4" />
              {property.bathrooms} baths
            </span>
            {property.sqft && (
              <span className="flex items-center gap-1">
                <Square className="w-4 h-4" />
                {formatNumber(property.sqft)} sqft
              </span>
            )}
          </div>
        </Link>

        {/* Why this ranked section */}
        {showBreakdown && topReasons.length > 0 && (
          <div className="border-t pt-3 mt-2">
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center justify-between w-full text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              <span>Why this ranked #{rank}?</span>
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            
            {expanded && (
              <div className="mt-2 space-y-1.5">
                {topReasons.map((reason, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm">
                    <reason.icon className="w-4 h-4 text-primary" />
                    <span className="text-muted-foreground">{reason.text}</span>
                  </div>
                ))}
                
                {score_breakdown && (
                  <div className="mt-3 pt-2 border-t grid grid-cols-2 gap-2 text-xs">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Price:</span>
                      <span className="font-medium">{score_breakdown.price?.score || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Photos:</span>
                      <span className="font-medium">{score_breakdown.photos?.score || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Recency:</span>
                      <span className="font-medium">{score_breakdown.recency?.score || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Completeness:</span>
                      <span className="font-medium">{score_breakdown.completeness?.score || 0}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
